import { useEffect, useRef } from "react";

import { CompetencyImportProgress, CompetencyImportResult } from "@performyard/service-contracts";

import { useAsyncTaskStream } from "../../../hooks/useAsyncTaskStream";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type CompetencyImportWatcherProps = {
	taskId: string;
	attachmentMessageId: string;
	narrationMessageId: string;
};

const ANALYZING_MESSAGE = "Analyzing your document…";
const STREAM_LOST_ERROR = "Import connection lost. Please try uploading again.";
const RESULT_UNREADABLE_ERROR = "We couldn't read the import result. Please try uploading again.";
const IMPORT_FAILED_ERROR = "Something went wrong while importing your document. Please try again.";
const DEFAULT_SUMMARY = "Here are the competencies I pulled from your document.";
const NO_COMPETENCY_SUMMARY =
	"This file doesn't appear to contain competency information. Try uploading a competency framework, job description, or values document — or paste your framework directly into the chat.";

export const CompetencyImportWatcher = ({
	taskId,
	attachmentMessageId,
	narrationMessageId,
}: CompetencyImportWatcherProps): null => {
	const { task, error, isConnected, isRetrying, hasConnected } = useAsyncTaskStream(taskId);
	const doneRef = useRef(false);
	const {
		updateAttachmentState,
		updateImportNarrationMessage,
		setCompetencySuggestions,
		setCompetencyUploadPromptMessage,
		scrollToBottom,
	} = useAskPerformYardStore.getState();

	useEffect(() => {
		if (doneRef.current) {
			return;
		}
		const status = task?.status;

		const settleWithRetry = (message: string) => {
			doneRef.current = true;
			updateImportNarrationMessage(narrationMessageId, message, false);
			updateAttachmentState(attachmentMessageId, { state: "error", importComplete: true });
			setCompetencyUploadPromptMessage(narrationMessageId);
			scrollToBottom?.();
		};

		if (status === "completed") {
			const result = CompetencyImportResult.safeParse(task?.metadata?.result);
			if (!result.success) {
				settleWithRetry(RESULT_UNREADABLE_ERROR);
				return;
			}
			doneRef.current = true;
			const pages = result.data.pageCount;
			updateAttachmentState(attachmentMessageId, {
				state: "done",
				description: `PDF · ${pages} page${pages === 1 ? "" : "s"}`,
				importComplete: true,
			});
			if (result.data.suggestions.length > 0) {
				updateImportNarrationMessage(narrationMessageId, result.data.summary.trim() || DEFAULT_SUMMARY, false);
				setCompetencySuggestions(narrationMessageId, result.data.suggestions, true);
			} else {
				updateImportNarrationMessage(
					narrationMessageId,
					result.data.summary.trim() || NO_COMPETENCY_SUMMARY,
					false
				);
				setCompetencyUploadPromptMessage(narrationMessageId);
			}
			scrollToBottom?.();
			return;
		}
		if (status === "failed") {
			settleWithRetry(task?.error ?? IMPORT_FAILED_ERROR);
			return;
		}
		if (!isConnected && !isRetrying && (error !== null || hasConnected)) {
			settleWithRetry(STREAM_LOST_ERROR);
			return;
		}
		if (status === "processing") {
			const progress = CompetencyImportProgress.safeParse(task?.metadata);
			if (progress.success) {
				const text = progress.data.phase === "extracting" ? `${progress.data.message}…` : ANALYZING_MESSAGE;
				updateImportNarrationMessage(narrationMessageId, text, true);
			}
		}
	}, [
		task,
		attachmentMessageId,
		narrationMessageId,
		error,
		isConnected,
		isRetrying,
		hasConnected,
		updateAttachmentState,
		updateImportNarrationMessage,
		setCompetencySuggestions,
		setCompetencyUploadPromptMessage,
		scrollToBottom,
	]);

	return null;
};
